import { useRef, useState } from "react";
import { Download, Loader2, Image as ImageIcon } from "lucide-react";
import { getEmployerLogo, getEmployerLogoFallback, getEmployerInitials } from "../lib/employerLogo";

/* Brand colours — kept in sync with tailwind.config */
const NAVY = "#0B1B3D";
const NAVY_DEEP = "#06112B";
const SKY = "#38BDF8";
const ICE = "#F2F7FF";
const SLATE = "#94A3B8";

/* Wrap text into N lines respecting maxWidth. Returns an array of lines. */
function wrapText(ctx, text, maxWidth, maxLines) {
  const words = (text || "").split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const next = current ? current + " " + w : w;
    if (ctx.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = w;
      if (lines.length === maxLines - 1) break;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  // If we truncated, append ellipsis to last line
  const remaining = words.slice(lines.join(" ").split(/\s+/).length).join(" ");
  if (remaining && lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(last + "…").width > maxWidth && last.length) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = last + "…";
  }
  return lines;
}

/* Load an image with a CORS-friendly fetch. Resolves to null on failure. */
function loadImage(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.referrerPolicy = "no-referrer";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function fmtSalary(v) {
  if (v.salary_min && v.salary_max)
    return `£${v.salary_min.toLocaleString()} – £${v.salary_max.toLocaleString()}`;
  if (v.salary_min) return `From £${v.salary_min.toLocaleString()}`;
  return "Salary on application";
}

/* Round-rect helper (Safari < 15 lacks roundRect). */
function rrect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export default function VacancyPoster({ vacancy }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function render() {
    setBusy(true);
    try {
      const W = 1080;
      const H = 1350;
      const canvas = canvasRef.current || document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");

      /* Background gradient (navy) */
      const grd = ctx.createLinearGradient(0, 0, 0, H);
      grd.addColorStop(0, NAVY);
      grd.addColorStop(1, NAVY_DEEP);
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, W, H);

      /* Subtle grid dots */
      ctx.fillStyle = "rgba(56,189,248,0.06)";
      for (let x = 40; x < W; x += 48) {
        for (let y = 40; y < H; y += 48) {
          ctx.fillRect(x, y, 2, 2);
        }
      }

      /* Top sky stripe */
      ctx.fillStyle = SKY;
      ctx.fillRect(0, 0, W, 14);

      /* Header: logo + brand */
      const padX = 80;
      const headerY = 80;
      const logoSize = 120;
      // Logo box
      rrect(ctx, padX, headerY, logoSize, logoSize, 16);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();

      const logoUrl = getEmployerLogo(vacancy.employer_name, vacancy.employer_logo);
      let logo = await loadImage(logoUrl);
      if (!logo) {
        const fb = getEmployerLogoFallback(vacancy.employer_name);
        logo = await loadImage(fb);
      }
      if (logo) {
        // contain inside logoSize with 12px padding
        const inner = logoSize - 24;
        const ratio = Math.min(inner / logo.width, inner / logo.height);
        const lw = logo.width * ratio;
        const lh = logo.height * ratio;
        ctx.drawImage(logo, padX + (logoSize - lw) / 2, headerY + (logoSize - lh) / 2, lw, lh);
      } else {
        ctx.fillStyle = SKY;
        ctx.font = "bold 54px 'Inter', sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(getEmployerInitials(vacancy.employer_name), padX + logoSize / 2, headerY + logoSize / 2);
        ctx.textAlign = "start";
        ctx.textBaseline = "alphabetic";
      }

      // Brand top-right
      ctx.fillStyle = ICE;
      ctx.font = "700 26px 'Inter', system-ui, sans-serif";
      ctx.textAlign = "right";
      ctx.fillText("RAILWAY", W - padX, headerY + 48);
      ctx.fillStyle = SKY;
      ctx.fillText("CAREERS · UK", W - padX, headerY + 82);
      ctx.textAlign = "start";

      /* Eyebrow */
      const eyebrowY = headerY + logoSize + 100;
      ctx.fillStyle = SKY;
      ctx.font = "700 22px 'Inter', system-ui, sans-serif";
      ctx.fillText("WE'RE HIRING", padX, eyebrowY);
      // Underline tick
      ctx.fillRect(padX, eyebrowY + 14, 56, 4);

      /* Title (max 3 lines) */
      ctx.fillStyle = ICE;
      ctx.font = "800 88px 'Inter', system-ui, sans-serif";
      const titleLines = wrapText(ctx, vacancy.title || "Vacancy", W - padX * 2, 3);
      let cursorY = eyebrowY + 110;
      titleLines.forEach((line) => {
        ctx.fillText(line, padX, cursorY);
        cursorY += 100;
      });

      /* Employer + location */
      ctx.fillStyle = ICE;
      ctx.font = "600 36px 'Inter', system-ui, sans-serif";
      cursorY += 20;
      ctx.fillText(vacancy.employer_name || "", padX, cursorY);
      cursorY += 50;
      ctx.fillStyle = SLATE;
      ctx.font = "500 28px 'Inter', system-ui, sans-serif";
      const subline = [
        vacancy.location,
        (vacancy.employment_type || "").replace(/-/g, " "),
        (vacancy.role_category || "").replace(/-/g, " "),
      ]
        .filter(Boolean)
        .join("  ·  ");
      ctx.fillText(subline, padX, cursorY);

      /* Salary chip */
      const salary = fmtSalary(vacancy);
      ctx.font = "800 40px 'Inter', system-ui, sans-serif";
      const salW = ctx.measureText(salary).width + 56;
      const salH = 80;
      const salY = cursorY + 50;
      rrect(ctx, padX, salY, salW, salH, 14);
      ctx.fillStyle = SKY;
      ctx.fill();
      ctx.fillStyle = NAVY;
      ctx.fillText(salary, padX + 28, salY + 54);

      /* Footer band */
      const footY = H - 200;
      ctx.fillStyle = "rgba(56,189,248,0.10)";
      ctx.fillRect(0, footY, W, 200);
      ctx.fillStyle = "rgba(56,189,248,0.5)";
      ctx.fillRect(0, footY, W, 2);

      ctx.fillStyle = ICE;
      ctx.font = "700 30px 'Inter', system-ui, sans-serif";
      ctx.fillText("APPLY AT", padX, footY + 64);
      ctx.fillStyle = SKY;
      ctx.font = "800 52px 'Inter', system-ui, sans-serif";
      ctx.fillText("railwaycareers.co.uk", padX, footY + 124);

      ctx.fillStyle = SLATE;
      ctx.font = "500 22px 'Inter', system-ui, sans-serif";
      ctx.textAlign = "right";
      const ref = (vacancy.id || "").slice(0, 8).toUpperCase();
      if (ref) ctx.fillText(`REF · ${ref}`, W - padX, footY + 64);
      if (vacancy.closing_date) {
        ctx.fillText(`Closes ${vacancy.closing_date}`, W - padX, footY + 100);
      }
      ctx.textAlign = "start";

      // Trigger download
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
      if (!blob) throw new Error("Could not render poster");
      const safeTitle = (vacancy.title || "vacancy")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      const filename = `railwaycareers-${safeTitle}-${ref || "poster"}.png`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={render}
        disabled={busy}
        data-testid="vacancy-poster-download"
        className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] bg-sky/15 text-sky border border-sky/40 hover:bg-sky hover:text-nav transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
        {busy ? "Rendering…" : "Download poster"}
        {!busy && <Download className="w-4 h-4" />}
      </button>
      <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
    </>
  );
}
